import type { CardAddons } from '@/pages/booking-funnel/packageConfiguration/types/packageData'
import type { BookingObject } from './types'
import { format, addDays } from 'date-fns'

type CreateBookingParams = {
  date: Date
  attendees: number
  userData: {
    company?: string
    firstName: string
    lastName: string
    email: string
    phone: string
    nbGuestRooms: number
  }
  eventName: string
  comments?: string
  packageId: string
  functionRoomId: string
  functionRoomSetupName: string
  functionRoomEvents: {
    id: string
    startTime: string
    endTime: string
    eventClassificationId: string
  }[]
  selectedAddons?: CardAddons
}

type AdditionalCreateBookingParams = {
  xObjectID?: string
  xObjectType?: string
}

export function createBooking(
  params: CreateBookingParams,
  redirectUrl: string,
  languageCode?: string,
  additionalParameters?: AdditionalCreateBookingParams,
): BookingObject {
  const guestRoomBlocks: BookingObject['guestRoomBlocks'] = []

  if (params.userData.nbGuestRooms > 0) {
    guestRoomBlocks.push({
      nights: [
        {
          patternDay: 0,
          guestrooms: [
            {
              roomCode: 'ROH',
              occupancyQuantity: [
                {
                  guestRoomOccupancyType: 'SingleOccupancy',
                  quantity: params.userData.nbGuestRooms,
                },
              ],
            },
          ],
        },
      ],
    })
  }

  /* If we have a guest room, we need at least one night */
  const departureDate =
    params.userData.nbGuestRooms > 0
      ? format(addDays(params.date, 1), 'yyyy-MM-dd')
      : format(params.date, 'yyyy-MM-dd')

  return {
    name: params.eventName,
    xRedirectPath: redirectUrl,
    xLanguageCode: languageCode,
    arrivalDate: format(params.date, 'yyyy-MM-dd'),
    departureDate,
    xObjectID: additionalParameters?.xObjectID,
    xObjectType: additionalParameters?.xObjectType,
    comments: params.comments,
    companyName: params.userData.company || '', // To confirm
    primaryContact: {
      company: params.userData.company,
      firstName: params.userData.firstName,
      lastName: params.userData.lastName,
      email: params.userData.email,
      phone: params.userData.phone,
    },
    packages: [
      {
        id: params.packageId,
        days: [
          {
            patternDay: 0,
            attendees: params.attendees,
            functionRoomId: params.functionRoomId,
            functionRoomSetupName: params.functionRoomSetupName,
            events: params.functionRoomEvents.map((event) => {
              const addons = []
              const matchingAddons = params.selectedAddons?.[event.id]
              if (matchingAddons) {
                addons.push(
                  ...matchingAddons.map((addon) => {
                    return {
                      id: addon.addonId,
                      quantity: addon.quantity,
                    }
                  }),
                )
              }
              return {
                eventClassificationId: event.eventClassificationId,
                startTime: event.startTime,
                endTime: event.endTime,
                addOnItems: addons,
              }
            }),
          },
        ],
      },
    ],
    guestRoomBlocks,
  }
}
